import { useEffect, useRef, useState } from "react";
import {
  TrendingUp, Leaf, ShieldCheck, DollarSign, Recycle, ChevronDown
} from "lucide-react";
import Footer from "./Footer";

import globeImg    from "../assets/globe.png";
import dppRingImg  from "../assets/dpp-ring.png";
import timelineImg from "../assets/espr-timeline.png";
import adminAppImg from "../assets/dpp-dashboard.png";
import scmImg      from "../assets/flow-graph.png";
import endUserImg  from "../assets/qr-scanning.png";

function useInView(threshold = 0.15, rootMargin = "0px") {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold, rootMargin }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

 
/* ══════════════════════════════════
   STICKY SECONDARY NAVBAR
══════════════════════════════════ */
const NAV_ITEMS = [
  { label: "Architecture",            id: "arch-section" },
  { label: "Applications",            id: "apps-section" },
  { label: "ESPR & Circular Economy", id: "espr-section" },
  { label: "ESPR Timeline",           id: "timeline-section" },
];
 
function SecondaryNav() {
  const [active,   setActive]   = useState(null);
  const [hovered,  setHovered]  = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
 
  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 80);
      for (let i = NAV_ITEMS.length - 1; i >= 0; i--) {
        const el = document.getElementById(NAV_ITEMS[i].id);
        if (el && el.getBoundingClientRect().top <= 160) {
          setActive(NAV_ITEMS[i].id); return;
        }
      }
      setActive(null);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
 
  // Close dropdown when clicking outside
  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest("#snav-dropdown")) setDropOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);
 
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 120, behavior: "smooth" });
    setActive(id);
    setDropOpen(false);
  };
 
  const activeLabel = NAV_ITEMS.find(n => n.id === active)?.label || "Navigate to...";
 
  return (
    <>
      <style>{`
        @keyframes navSlideDown {
          from { opacity:0; transform:translateY(-12px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes dropDown {
          from { opacity:0; transform:translateY(-8px) scaleY(0.92); }
          to   { opacity:1; transform:translateY(0) scaleY(1); }
        }
        .snav-pill {
          position:relative; padding:8px 18px; border-radius:999px;
          font-size:13px; font-weight:600; cursor:pointer; border:none;
          background:transparent; transition:color 0.2s; white-space:nowrap;
          letter-spacing:0.01em;
        }
        .snav-pill::after {
          content:''; position:absolute; bottom:4px; left:50%; right:50%;
          height:2px; background:#2563eb; border-radius:999px;
          transition:left 0.3s cubic-bezier(0.34,1.56,0.64,1),
                      right 0.3s cubic-bezier(0.34,1.56,0.64,1);
        }
        .snav-pill.active::after,.snav-pill.hov::after { left:18px; right:18px; }
        .snav-dot {
          width:6px; height:6px; border-radius:50%; background:#2563eb;
          display:inline-block; margin-right:6px;
          animation:dotPulse 1.8s ease-in-out infinite;
        }
        @keyframes dotPulse {
          0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(0.6)}
        }
      `}</style>
 
      <div style={{
        position:"fixed", top:66, left:0, right:0, zIndex:40,
        display:"flex", justifyContent:"center",
        padding:"8px 12px",
        animation:"navSlideDown 0.4s ease both",
        pointerEvents:"none",
      }}>
 
        {/* ── MOBILE: compact dropdown ── */}
        <div
          id="snav-dropdown"
          className="block sm:hidden"
          style={{ pointerEvents:"auto", width:"100%", maxWidth:340, position:"relative" }}
        >
          {/* Trigger button */}
          <button
            onClick={() => setDropOpen(v => !v)}
            style={{
              width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",
              padding:"10px 16px", borderRadius:12,
              background: scrolled ? "rgba(255,255,255,0.97)" : "rgba(255,255,255,0.90)",
              backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
              boxShadow:"0 4px 20px rgba(0,0,0,0.12), 0 0 0 1px rgba(37,99,235,0.10)",
              border:"none", cursor:"pointer",
              transition:"box-shadow 0.3s, background 0.3s",
            }}
          >
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              {active && <span style={{
                width:8, height:8, borderRadius:"50%", background:"#2563eb", flexShrink:0,
                animation:"dotPulse 1.8s ease-in-out infinite",
              }} />}
              <span style={{ fontSize:13, fontWeight:600, color: active ? "#2563eb" : "#64748b" }}>
                {activeLabel}
              </span>
            </div>
            <svg
              width="16" height="16" viewBox="0 0 16 16" fill="none"
              style={{ transform: dropOpen ? "rotate(180deg)" : "rotate(0deg)", transition:"transform 0.25s ease", flexShrink:0 }}
            >
              <path d="M4 6l4 4 4-4" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
 
          {/* Dropdown menu */}
          {dropOpen && (
            <div style={{
              position:"absolute", top:"calc(100% + 6px)", left:0, right:0,
              background:"rgba(255,255,255,0.98)",
              backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
              borderRadius:12, overflow:"hidden",
              boxShadow:"0 8px 32px rgba(0,0,0,0.14), 0 0 0 1px rgba(37,99,235,0.08)",
              animation:"dropDown 0.2s ease both",
              transformOrigin:"top center",
            }}>
              {NAV_ITEMS.map((item, i) => {
                const isActive = active === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollTo(item.id)}
                    style={{
                      width:"100%", textAlign:"left",
                      padding:"12px 16px",
                      display:"flex", alignItems:"center", gap:10,
                      background: isActive ? "#eff6ff" : "transparent",
                      border:"none", cursor:"pointer",
                      borderBottom: i < NAV_ITEMS.length - 1 ? "1px solid #f1f5f9" : "none",
                      transition:"background 0.15s",
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background="#f8fafc"; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background="transparent"; }}
                  >
                    <div style={{
                      width:6, height:6, borderRadius:"50%", flexShrink:0,
                      background: isActive ? "#2563eb" : "#cbd5e1",
                    }} />
                    <span style={{
                      fontSize:13, fontWeight: isActive ? 700 : 500,
                      color: isActive ? "#2563eb" : "#475569",
                    }}>
                      {item.label}
                    </span>
                    {isActive && (
                      <svg style={{ marginLeft:"auto" }} width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M2.5 7l3 3 6-6" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
 
        {/* ── DESKTOP: pill bar ── */}
        <div
          className="hidden sm:flex"
          style={{
            alignItems:"center", gap:4, padding:"5px 6px",
            borderRadius:999,
            background: scrolled ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.85)",
            backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
            boxShadow:"0 4px 24px rgba(0,0,0,0.13), 0 0 0 1px rgba(37,99,235,0.1)",
            transition:"box-shadow 0.3s, background 0.3s",
            pointerEvents:"auto",
          }}
        >
          {NAV_ITEMS.map((item) => {
            const isActive = active  === item.id;
            const isHov    = hovered === item.id;
            return (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                onMouseEnter={() => setHovered(item.id)}
                onMouseLeave={() => setHovered(null)}
                className={`snav-pill ${isActive ? "active" : ""} ${isHov && !isActive ? "hov" : ""}`}
                style={{ color: isActive ? "#2563eb" : isHov ? "#1e40af" : "#64748b" }}
              >
                {isActive && <span className="snav-dot" />}
                {item.label}
              </button>
            );
          })}
        </div>
 
      </div>
    </>
  );
}
 


/* ══════════════════════════════════
   SECTION 1 - Hero
══════════════════════════════════ */
function HeroSection() {
  const [mousePos, setMousePos] = useState({ x:0, y:0 });
  const [visible,  setVisible]  = useState(false);

  useEffect(() => {
    setTimeout(() => setVisible(true), 100);
    const onMouse = (e) => setMousePos({
      x: (e.clientX / window.innerWidth  - 0.5) * 18,
      y: (e.clientY / window.innerHeight - 0.5) * 12,
    });
    window.addEventListener("mousemove", onMouse);
    return () => window.removeEventListener("mousemove", onMouse);
  }, []);

  const fade = (delay) => ({
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(24px)",
    transition: `opacity 0.65s ease ${delay}s, transform 0.65s ease ${delay}s`,
  });

  return (
    <section className="relative overflow-hidden bg-white">
      <div style={{
        position:"absolute", top:"5%", right:"-8%", width:"65%", height:"90%",
        // background:"radial-gradient(ellipse at center,rgba(219,234,254,0.65) 0%,rgba(255,255,255,0) 68%)",
        pointerEvents:"none",
      }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-20 pb-8 sm:pt-24 sm:pb-12">
        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-12">

          {/* LEFT */}
          <div className="flex-1 text-center md:text-left order-2 md:order-1">
            <p style={{ ...fade(0.1), fontSize:13, fontWeight:800, letterSpacing:"0.18em", color:"#2563eb", textTransform:"uppercase", marginBottom:8 }}>
              PROVINYX
            </p>
            <h1 style={{ ...fade(0.2), fontSize:"clamp(34px,6vw,90px)", fontWeight:900, lineHeight:1.05, color:"#0f172a", marginBottom:14 }}>
              Digital Product<br />Passport
            </h1>
            <p style={{ ...fade(0.35), fontSize:"clamp(14px,1.4vw,18px)", color:"#64748b", lineHeight:1.7, maxWidth:430, display:"block" }}
               className="md:mx-0">
              One product. One passport. Every detail - from creation to end of life.
            </p>
          </div>

          {/* RIGHT: globe */}
          <div className="flex-1 flex justify-center items-center order-1 md:order-2" style={{ ...fade(0.3) }}>
            <div style={{
              transform:`translate(${mousePos.x*0.35}px,${mousePos.y*0.35}px)`,
              transition:"transform 0.12s ease-out", position:"relative",
            }}>
              <div style={{ position:"absolute", inset:-20, borderRadius:"50%", border:"3.5px dashed rgba(37,99,235,0.18)", animation:"ringR 20s linear infinite" }} />
              <div style={{ position:"absolute", inset:-32, borderRadius:"50%", border:"2.5px dashed rgba(37,99,235,0.09)", animation:"ringR 32s linear infinite reverse" }} />
              <img src={globeImg} alt="Globe" style={{
                width:"clamp(160px,34vw,400px)",
                mixBlendMode:"multiply",
                display:"block",
              }} />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes ringR { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
      `}</style>
    </section>
  );
}

/* ══════════════════════════════════
   SECTION 2 - DPP Architecture
══════════════════════════════════ */
function AnimatedArrow() {
  return (
    <div style={{ display:"flex", justifyContent:"center", flexDirection:"column", alignItems:"center", margin:"4px 0" }}>
      <div style={{ width:2, height:14, background:"#93c5fd" }} />
      <div style={{ animation:"arrowBounce 1.2s ease-in-out infinite" }}>
        <ChevronDown size={20} color="#60a5fa" />
      </div>
      <style>{`
        @keyframes arrowBounce{0%,100%{transform:translateY(0);opacity:1}50%{transform:translateY(5px);opacity:0.5}}
      `}</style>
    </div>
  );
}

function ArchNode({ label, desc, visible, delay, hovered, onEnter, onLeave, fullWidth }) {
  return (
    <div onMouseEnter={onEnter} onMouseLeave={onLeave} style={{
      background: hovered ? "#1d4ed8" : "#3b82f6",
      borderRadius:12, padding:"16px 18px", color:"#fff", textAlign:"center",
      cursor:"default", flex: fullWidth ? "unset" : 1,
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(30px)",
      transition:`opacity 0.5s ease ${delay}s, transform 0.5s ease ${delay}s, background 0.25s, box-shadow 0.25s`,
      boxShadow: hovered ? "0 14px 40px rgba(37,99,235,0.45)" : "0 4px 20px rgba(37,99,235,0.18)",
    }}>
      <p style={{ fontWeight:700, fontSize:14, marginBottom:5 }}>{label}</p>
      <p style={{ fontSize:14, opacity:0.88, lineHeight:1.55 }}>{desc}</p>
    </div>
  );
}

function ArchSection() {
  const [ref, visible] = useInView(0.1);
  const [hov, setHov]  = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const nodes = [
    { id:"bc", label:"Blockchain layer",               desc:"Cryptographic hashes of data, Event timestamps, Smart contract rules, Consensus-validated transactions" },
    { id:"dc", label:"Data collection layer",          desc:"Captures structured product data at every lifecycle stage (materials, carbon footprint, events)" },
    { id:"di", label:"Digital identity layer",         desc:"Assigns every supply chain actor a cryptographic identity" },
    { id:"lu", label:"Lifecycle update layer",         desc:"Keeps the DPP live and current as the product moves through its entire lifecycle" },
    { id:"ai", label:"AI reporting and analytic layer", desc:"Transforms raw DPP lifecycle data into automated reports, compliance flags, and predictive insights" },
  ];

  return (
    <section id="arch-section" ref={ref} className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10 sm:mb-14" style={{
          opacity:visible?1:0, transform:visible?"translateY(0)":"translateY(24px)",
          transition:"opacity 0.6s, transform 0.6s",
        }}>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900">
            <span className="text-blue-600">PROVINYX</span> DPP Architecture
          </h2>
          <p className="text-gray-500 mt-3 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            Block by Block: Five integrated layers powering the future of transparent, verifiable product lifecycle management
          </p>
        </div>

        <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-8 shadow-lg border border-gray-100">
          {/* Top row: on mobile stack, on sm+ side by side */}
          {isMobile ? (
            <>
              <ArchNode label={nodes[0].label} desc={nodes[0].desc} visible={visible} delay={0.1} fullWidth
                hovered={hov==="bc"} onEnter={()=>setHov("bc")} onLeave={()=>setHov(null)} />
              <AnimatedArrow />
              <ArchNode label={nodes[1].label} desc={nodes[1].desc} visible={visible} delay={0.2} fullWidth
                hovered={hov==="dc"} onEnter={()=>setHov("dc")} onLeave={()=>setHov(null)} />
            </>
          ) : (
            <div style={{ display:"flex", alignItems:"stretch", gap:0 }}>
              <ArchNode label={nodes[0].label} desc={nodes[0].desc} visible={visible} delay={0.1}
                hovered={hov==="bc"} onEnter={()=>setHov("bc")} onLeave={()=>setHov(null)} />
              <div style={{
                display:"flex", alignItems:"center", padding:"0 4px",
                opacity:visible?1:0, transition:"opacity 0.5s ease 0.2s",
              }}>
                <div style={{ width:32, height:2, background:"#93c5fd", animation:"pulseW 1.5s ease-in-out infinite" }} />
                <style>{`@keyframes pulseW{0%,100%{opacity:1}50%{opacity:0.3}}`}</style>
              </div>
              <ArchNode label={nodes[1].label} desc={nodes[1].desc} visible={visible} delay={0.2}
                hovered={hov==="dc"} onEnter={()=>setHov("dc")} onLeave={()=>setHov(null)} />
            </div>
          )}

          <AnimatedArrow />

          <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
            {[{id:"di",d:0.35},{id:"lu",d:0.47},{id:"ai",d:0.59}].map(({id,d},i) => {
              const n = nodes.find(x=>x.id===id);
              return (
                <div key={id}>
                  <ArchNode label={n.label} desc={n.desc} visible={visible} delay={d} fullWidth
                    hovered={hov===id} onEnter={()=>setHov(id)} onLeave={()=>setHov(null)} />
                  {i < 2 && <AnimatedArrow />}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════
   SECTION 3 - Provinyx Apps
══════════════════════════════════ */
const apps = [
  { img:adminAppImg, title:"ADMIN APP",    subtitle:"A dashboard based data collection", user:"Manufacturer/Regulator/Recycler/Repairer" },
  { img:scmImg,      title:"PROVINYX SCM", subtitle:"Product tracking via geo location", user:"Supply Chain Managers" },
  { img:endUserImg,  title:"END USER APP", subtitle:"Consumer access to product data",    user:"End Consumers" },
];

function AppsSection() {
  const [ref, visible] = useInView(0.1);
  const [hovered, setHovered] = useState(null);

  return (
    <section id="apps-section" ref={ref} className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10 sm:mb-14" style={{
          opacity:visible?1:0, transform:visible?"translateY(0)":"translateY(24px)",
          transition:"opacity 0.6s, transform 0.6s",
        }}>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900">
            <span className="text-blue-600">PROVINYX</span> APPS
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
          {apps.map((app,i) => {
            const isHov = hovered === i;
            return (
              <div
                key={i}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? (isHov ? "translateY(-8px) scale(1.02)" : "translateY(0) scale(1)") : "translateY(40px)",
                  transition:`opacity 0.5s ease ${i*0.12}s, transform 0.35s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s, border-color 0.3s`,
                  borderRadius:16, border:`2px solid ${isHov?"#2563eb":"#e5e7eb"}`,
                  background:"#fff",
                 boxShadow: isHov
  ? "0 30px 40px rgba(37,99,235,0.18)"
  : "0 14px 12px rgba(0,0,0,0.08)",
                  cursor:"default", overflow:"hidden", padding:"20px 22px 10px 22px",
                }}
              >
                {i === 1 ? (
                  /* PROVINYX SCM: title top, full-width image below */
                  <>
                    <h3 style={{ fontWeight:900, fontSize:17, color:isHov?"#2563eb":"#0f172a", transition:"color 0.25s", marginBottom:12 }}>
                      {app.title}
                    </h3>
                    <div style={{
                      width:"100%", height:95, borderRadius:0, overflow:"hidden",
                      background:"#f8fafc", marginBottom:14,
                    }}>
                      <img src={app.img} alt={app.title} style={{
                        width:"100%", height:"100%", objectFit:"cover",
                        transform:isHov?"scale(1.05)":"scale(1)", transition:"transform 0.4s ease",
                      }} />
                    </div>
                  </>
                ) : (
                  /* Admin + End User: title left, image top-right */
                  <>
                    <h3 style={{ fontWeight:900, fontSize:17, color:isHov?"#2563eb":"#0f172a", transition:"color 0.25s", marginBottom:12 }}>
                      {app.title}
                    </h3>
                    <div style={{
                      width:"100%", height:95, borderRadius:0, overflow:"hidden",
                      background:"#f8fafc", marginBottom:14,
                    }}>
                      <img src={app.img} alt={app.title} style={{
                        width:"120%", height:"120%", objectFit:"contain",
                        transform:isHov?"scale(1.05)":"scale(1)", transition:"transform 0.4s ease",
                      }} />
                    </div>
                  </>
                )}
                <p style={{ color:"#475569", fontSize:15, lineHeight:1.65, marginBottom:6, fontWeight:500 }}>{app.subtitle}</p>
                {/* <p style={{ fontSize:13, color:"#64748b" }}>
                  <span style={{ fontWeight:700, color:"#0f172a" }}>User: </span>{app.user}
                </p> */}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════
   SECTION 4 - ESPR & Circular Economy
══════════════════════════════════ */
const esprGoals = [
  "Harmonize sustainability requirements Across the EU",
  "Extend lifecycles via durability and repairability",
  "Ensure transparency via Digital Product Passport",
  "Drive circular economy adoption",
  "Combat greenwashing with legally verified data",
];
const circularPoints = [
  { icon:TrendingUp,  text:"The circular economy is now a business imperative, not just a sustainability goal." },
  { icon:ShieldCheck, text:"Companies using circular principles are gaining a competitive advantage as the market grows through 2033." },
  { icon:Recycle,     text:"Designing for longevity, repair, reuse, and material recovery helps reduce costs and raw material dependence." },
  { icon:DollarSign,  text:"Circular business models could create $4.5 trillion in economic value by 2030, showing that sustainability and profitability can work together." },
];

function EsprSection() {
  const [headRef,  headVisible]  = useInView(0.2,  "0px 0px -80px 0px");
  const [cardsRef, cardsVisible] = useInView(0.25, "0px 0px -60px 0px");
  const [rightRef, rightVisible] = useInView(0.25, "0px 0px -60px 0px");
  const [activeRight, setActiveRight] = useState(null);

  return (
    <section id="espr-section" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div ref={headRef} className="text-center mb-10 sm:mb-14" style={{
          opacity:headVisible?1:0, transform:headVisible?"translateY(0)":"translateY(24px)",
          transition:"opacity 0.6s, transform 0.6s",
        }}>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900">
            <span className="text-blue-600">ESPR</span> and Circular Economy
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

          {/* LEFT: 5 goal cards */}
          <div ref={cardsRef} style={{ overflow:"hidden", paddingLeft:2 }}>
            <h3 className="font-bold text-gray-900 text-lg sm:text-xl mb-5 text-center" style={{
              opacity:cardsVisible?1:0,
              transform:cardsVisible?"translateX(0)":"translateX(-80px)",
              transition:"opacity 0.65s ease 0.05s, transform 0.65s ease 0.05s",
            }}>
              5 core goals of ESPR
            </h3>
            <div className="space-y-3">
              {esprGoals.map((goal,i) => (
                <div key={i} style={{
                  opacity: cardsVisible ? 1 : 0,
                  transform: cardsVisible ? "translateX(0)" : "translateX(-110px)",
                  transition:`opacity 0.75s ease ${0.1+i*0.25}s, transform 0.75s cubic-bezier(0.22,1,0.36,1) ${0.1+i*0.25}s`,
                  background:"#fff", borderRadius:11, padding:"12px 16px",
                  display:"flex", alignItems:"center", gap:12,
                  border:"1px solid #e5e7eb", boxShadow:"0 2px 8px rgba(0,0,0,0.04)",
                }}>
                  <div style={{
                    width:30, height:30, borderRadius:7,
                    background:"#2563eb", color:"#fff",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontWeight:700, fontSize:13, flexShrink:0,
                  }}>{i+1}</div>
                  <p className="text-gray-700 text-sm font-medium leading-snug">{goal}</p>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: 4 hover blocks */}
          <div ref={rightRef} className="space-y-2 mt-0 lg:mt-10">
            {circularPoints.map((pt,i) => {
              const Icon     = pt.icon;
              const isActive = activeRight===i;
              return (
                <div key={i}
                  onMouseEnter={() => setActiveRight(i)}
                  onMouseLeave={() => setActiveRight(null)}
                  style={{
                    opacity:rightVisible?1:0,
                    transform:rightVisible?"translateY(0)":"translateY(20px)",
                    transition:`opacity 0.5s ease ${0.1+i*0.12}s, transform 0.5s ease ${0.1+i*0.12}s, background 0.25s, border-color 0.25s`,
                    padding:"14px 16px", borderRadius:12,
                    border:`1px solid ${isActive?"#bfdbfe":"transparent"}`,
                    background:isActive?"#eff6ff":"transparent",
                    display:"flex", alignItems:"flex-start", gap:12, cursor:"default",
                  }}>
                  <div style={{
                    width:40, height:40, borderRadius:10, flexShrink:0,
                    background:isActive?"#2563eb":"#dbeafe",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    transition:"background 0.25s",
                  }}>
                    <Icon size={18} color={isActive?"#fff":"#2563eb"} />
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed pt-0.5">{pt.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════
   SECTION 5 - DPP through Circular Economy
══════════════════════════════════ */
const dppPoints = [
  { icon:TrendingUp,  title:"Transparency as Strategy", desc:"Businesses that embrace transparency will be better positioned to succeed in the circular economy." },
  { icon:ShieldCheck, title:"Verified Digital Identity", desc:"A Digital Product Passport gives products a verified identity that builds consumer trust, supports compliance, and creates value across the lifecycle." },
  { icon:Recycle,     title:"Design to Disassembly",     desc:"From design to disassembly, products can carry useful information that helps them keep moving through the system efficiently." },
];

function DppCircularSection() {
  const [textRef, textVisible] = useInView(0.1);
  const [imgRef,  imgVisible]  = useInView(0.1);
  const [activeIdx, setActiveIdx] = useState(0);
  const spunRef = useRef(new Set());
  const [spinningIdx, setSpinningIdx] = useState(null);

  const handleHover = (i) => {
    setActiveIdx(i);
    if (!spunRef.current.has(i)) {
      spunRef.current.add(i);
      setSpinningIdx(i);
      setTimeout(() => setSpinningIdx(null), 600);
    }
  };

  return (
    <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 sm:mb-14">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 mb-3">
            DPP through <span className="text-blue-600">Circular Economy</span>
          </h2>
          <p className="text-gray-500 text-base sm:text-lg">A Smarter System for a More Sustainable Future</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* LEFT: content */}
          <div ref={textRef} className="space-y-2">
            {dppPoints.map((pt,i) => {
              const Icon=pt.icon; const isActive=activeIdx===i; const isSpinning=spinningIdx===i;
              return (
                <div key={i} onMouseEnter={() => handleHover(i)} style={{
                  opacity:textVisible?1:0,
                  transform:textVisible?"translateY(0)":"translateY(16px)",
                  transition:`opacity 0.5s ease ${0.1+i*0.1}s, transform 0.5s ease ${0.1+i*0.1}s, background 0.25s`,
                  padding:"16px 18px", borderRadius:14,
                  border:`1px solid ${isActive?"#bfdbfe":"transparent"}`,
                  background:isActive?"#eff6ff":"transparent",
                  display:"flex", alignItems:"flex-start", gap:14, cursor:"default",
                }}>
                  <div className={isSpinning?"spin-once":""} style={{
                    width:44, height:44, borderRadius:12, flexShrink:0,
                    background:isActive?"#2563eb":"#dbeafe",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    transition:"background 0.25s",
                  }}>
                    <Icon size={20} color={isActive?"#fff":"#2563eb"} />
                  </div>
                  <div>
                    <h4 style={{ fontWeight:600, fontSize:15, marginBottom:3, color:isActive?"#2563eb":"#0f172a", transition:"color 0.2s" }}>{pt.title}</h4>
                    <p className="text-gray-500 text-sm leading-relaxed">{pt.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* RIGHT: ring image */}
          <div ref={imgRef} style={{
            opacity:imgVisible?1:0,
            transform:imgVisible?"translateX(0)":"translateX(50px)",
            transition:"opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s",
            display:"flex", justifyContent:"center",
          }}>
            <img src={dppRingImg} alt="DPP Ring" style={{
              width:"100%", maxWidth:550,
            //   filter:"drop-shadow(0 16px 40px rgba(37,99,235,0.12))",
            }} />
          </div>
        </div>
      </div>
      <style>{`
        @keyframes spinOnce{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        .spin-once{animation:spinOnce 0.55s cubic-bezier(0.4,0,0.2,1) forwards}
      `}</style>
    </section>
  );
}

/* ══════════════════════════════════
   SECTION 6 - ESPR Timeline
══════════════════════════════════ */
function TimelineSection() {
  const [headRef, headVisible] = useInView(0.1);
  const [imgRef,  imgVisible]  = useInView(0.1);

  return (
    <section id="timeline-section" className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div ref={headRef} className="mb-8 sm:mb-12 text-center">
          <h2 style={{
            fontSize:"clamp(24px,4vw,48px)", fontWeight:900, color:"#0f172a",
            opacity:headVisible?1:0,
            transform:headVisible?"translateX(0)":"translateX(-60px)",
            transition:"opacity 0.75s ease, transform 0.75s cubic-bezier(0.22,1,0.36,1)",
            lineHeight:1.2,
          }}>
            Target Sectors and{" "}
            <span style={{
              color:"#2563eb", display:"inline-block",
              opacity:headVisible?1:0,
              transform:headVisible?"translateX(0)":"translateX(-30px)",
              transition:"opacity 0.75s ease 0.18s, transform 0.75s ease 0.18s",
            }}>ESPR Timeline</span>
          </h2>
        </div>

        <div ref={imgRef} style={{
          opacity:imgVisible?1:0,
          transform:imgVisible?"translateY(0) scale(1)":"translateY(50px) scale(0.97)",
          transition:"opacity 0.8s ease 0.1s, transform 0.8s cubic-bezier(0.25,0.46,0.45,0.94) 0.1s",
          borderRadius:16, overflow:"hidden",
          boxShadow:"0 20px 70px rgba(0,0,0,0.10)",
        }}>
          <img src={timelineImg} alt="ESPR Timeline" style={{ width:"100%", display:"block" }} />
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════
   SECTION 7 - At Provinyx
══════════════════════════════════ */
const beliefPoints = [
  { icon:ShieldCheck, highlight:"EU compliance",             prefix:"We simplify" },
  { icon:TrendingUp,  highlight:"data into trust",           prefix:"We turn" },
  { icon:Leaf,        highlight:"your business",             prefix:"We future-proof" },
  { icon:DollarSign,  highlight:"sustainability profitable", prefix:"We make" },
];

function AtProvinyx() {
  const [ref, visible]  = useInView(0.1);
  const [hovered, setHovered] = useState(null);

  return (
    <section ref={ref} className="relative py-16 sm:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden"
      style={{ background:"linear-gradient(135deg,#1e40af 0%,#2563eb 50%,#4338ca 100%)" }}>
      <div className="absolute top-0 left-0 w-72 sm:w-96 h-72 sm:h-96 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-72 sm:w-96 h-72 sm:h-96 bg-white/5 rounded-full translate-x-1/2 translate-y-1/2 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">

          {/* LEFT */}
          <div style={{ opacity:visible?1:0, transform:visible?"translateX(0)":"translateX(-50px)", transition:"opacity 0.7s, transform 0.7s" }}>
            <p className="text-blue-200 text-xs sm:text-sm font-semibold uppercase tracking-widest mb-3 sm:mb-4">Our Philosophy</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight mb-6 sm:mb-8">
              At <span className="text-blue-200">PROVINYX</span>, we believe that compliance and innovation go hand in hand.
            </h2>
            <div className="w-14 sm:w-16 h-1 bg-blue-300 rounded-full mb-6 sm:mb-8" />
            <p className="text-blue-100 text-base sm:text-lg italic leading-relaxed">
              "The circular economy is not just a policy direction - it is a market opportunity."
            </p>
          </div>

          {/* RIGHT: 2x2 glass cards */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4"
            style={{ opacity:visible?1:0, transform:visible?"translateX(0)":"translateX(50px)", transition:"opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s" }}>
            {beliefPoints.map((pt,i) => {
              const Icon=pt.icon; const isHov=hovered===i;
              return (
                <div key={i}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    borderRadius:16, padding:"18px 16px",
                    background:isHov?"rgba(255,255,255,0.22)":"rgba(255,255,255,0.10)",
                    border:`1px solid ${isHov?"rgba(255,255,255,0.5)":"rgba(255,255,255,0.15)"}`,
                    transform:isHov?"translateY(-6px) scale(1.03)":"translateY(0) scale(1)",
                    transition:"all 0.3s cubic-bezier(0.34,1.56,0.64,1)",
                    cursor:"default", backdropFilter:"blur(8px)",
                  }}>
                  <div style={{
                    width:40, height:40, borderRadius:10,
                    background:isHov?"#fff":"rgba(255,255,255,0.15)",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    marginBottom:12, transition:"background 0.3s", flexShrink:0,
                  }}>
                    <Icon size={20} color={isHov?"#2563eb":"#fff"} />
                  </div>
                  <p className="text-white text-xs sm:text-sm leading-snug">
                    {pt.prefix} <span className="font-bold text-blue-200">{pt.highlight}.</span>
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <div className="pt-16">
      <SecondaryNav />
      <div style={{ paddingTop:48 }}>
        <HeroSection />
        <ArchSection />
        <AppsSection />
        <EsprSection />
        <DppCircularSection />
        <TimelineSection />
        <AtProvinyx />
        <Footer />
      </div>
    </div>
  );
}